import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { queryKeys } from './queryKeys';
import { useDropRoundEditCacheForCompany } from './useCapTable';
import { assetTransactionService } from '@/services/assetTransactionService';
import type {
  AssetTransactionApproveRequest,
  AssetTransactionCancelRequest,
  AssetTransactionCreateRequest,
  AssetTransactionUpdateRequest,
} from '@/types/assetTransaction';

export const useAssetTransaction = (id: string | undefined) => {
  return useApiQuery(
    queryKeys.assetTransactions.detail(id!),
    () => assetTransactionService.getById(id!),
    {
      enabled: !!id,
    }
  );
};

export const useAssetTransactionJournalEntries = (
  transactionId: string,
  enabled: boolean = true
) => {
  return useApiQuery(
    [...queryKeys.assetTransactions.detail(transactionId), 'journal-entries'],
    () => assetTransactionService.getJournalEntries(transactionId),
    {
      enabled: enabled && !!transactionId,
    }
  );
};

export const useAssetTransactions = (companyId: string, enabled = true) => {
  return useApiQuery(
    queryKeys.assetTransactions.list(companyId),
    () => assetTransactionService.list(companyId),
    {
      enabled: !!companyId && enabled,
      showErrorToast: false,
      staleTime: 2 * 60 * 1000,
    }
  );
};

export const useCreateAssetTransaction = () => {
  return useApiMutation(
    ({ data }: { data: AssetTransactionCreateRequest; companyId: string }) =>
      assetTransactionService.create(data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.assetTransactions.list(variables.companyId),
        queryKeys.capTable.company(variables.companyId),
        [...queryKeys.capTable.all, 'snapshot', variables.companyId],
      ],
      successMessage: 'Asset sale transaction created successfully',
    }
  );
};

export const useUpdateAssetTransaction = () => {
  return useApiMutation(
    ({
      transactionId,
      data,
    }: {
      transactionId: string;
      companyId: string;
      data: AssetTransactionUpdateRequest;
    }) => assetTransactionService.update(transactionId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.assetTransactions.detail(variables.transactionId),
        queryKeys.assetTransactions.list(variables.companyId),
        queryKeys.capTable.company(variables.companyId),
        [...queryKeys.capTable.all, 'snapshot', variables.companyId],
      ],
      successMessage: 'Asset sale transaction updated successfully',
    }
  );
};

export const useApproveAssetTransaction = () => {
  const dropRoundEditCache = useDropRoundEditCacheForCompany();
  return useApiMutation(
    ({
      transactionId,
      data,
    }: {
      transactionId: string;
      companyId: string;
      data: AssetTransactionApproveRequest;
    }) => assetTransactionService.approve(transactionId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.assetTransactions.detail(variables.transactionId),
        queryKeys.assetTransactions.list(variables.companyId),
        queryKeys.capTable.company(variables.companyId),
        [...queryKeys.capTable.all, 'snapshot', variables.companyId],
        queryKeys.portfolio.detail(variables.companyId),
        [...queryKeys.portfolio.detail(variables.companyId), 'valuations'],
        queryKeys.fundTransactions.all,
      ],
      onSuccessCallback: (_data, variables) => {
        dropRoundEditCache(variables.companyId);
      },
      successMessage: 'Asset transaction approved successfully',
    }
  );
};

export const useCancelAssetTransaction = () => {
  const dropRoundEditCache = useDropRoundEditCacheForCompany();
  return useApiMutation(
    ({
      transactionId,
      data,
    }: {
      transactionId: string;
      companyId: string;
      data: AssetTransactionCancelRequest;
    }) => assetTransactionService.cancel(transactionId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.assetTransactions.detail(variables.transactionId),
        queryKeys.assetTransactions.list(variables.companyId),
        queryKeys.capTable.company(variables.companyId),
        [...queryKeys.capTable.all, 'snapshot', variables.companyId],
      ],
      onSuccessCallback: (_data, variables) => {
        dropRoundEditCache(variables.companyId);
      },
      successMessage: 'Asset transaction cancelled successfully',
    }
  );
};
